import { useCallback, useEffect, useRef } from "react";
import { shallowEqual, useSelector } from "react-redux";

import { isForecastTrumpingSource } from "constants/settings.constants";
import { generateMapRequest } from "entities/map/requests/factory";
import { useSetAtom } from "jotai";
import { RootState } from "store/rootReducer";
import { selectCheckedGlobalForecasts, useForecastStore } from "stores/forecast";
import {
  type IMapRequest,
  selectFacilityHasToConfirmSize,
  selectHasToConfirmSize,
  useMapStore
} from "stores/map";
import { selectUserSettingsOpen, usePanelStore } from "stores/panelStore";
import { useDebouncedCallback } from "use-debounce";
import { buildForecastTrumpingSettings } from "utils/data-table";

import { useNormalizationUnitCorrection } from "hooks/useNormalizationUnitCorrection";

import { withLegacyLockKeys } from "components/filter/util/colorLockKey";
import { IColorPalette } from "components/user-settings/models";

import { EMPTY_MAP_RESULT } from "../../../api/map";
import { facilityMapRequestAtom } from "../../../atoms/map/request/facilities";
import { wellMapRequestAtom } from "../../../atoms/map/request/wells";
import { CLEAR_MAP_ID } from "../../../constants/app.constants";
import { EntityKind } from "../../../models/entityKind";
import { useFacilityGroupBy } from "./useFacilityGroupBy";
import { useFacilityMapUpdater } from "./useFacilityMapUpdater";
import { useWellMapUpdater } from "./useWellMapUpdater";

/**
 * Custom hook to manage map requests for both wells and facilities.
 * Handles the generation of map requests and triggers updates when dependencies change.
 *
 * This hook encapsulates:
 * - Building well and facility map requests with proper parameters
 * - Triggering map updates when filters, grouping, or settings change
 */
export function useMapRequestManager() {
  const setTxnId = useMapStore((s) => s.setTxnId);

  const setWellMapRequest = useSetAtom(wellMapRequestAtom);
  const setFacilityMapRequest = useSetAtom(facilityMapRequestAtom);

  const hasToConfirmWellsSize = useMapStore(selectHasToConfirmSize);
  const hasToConfirmFacilitiesSize = useMapStore(selectFacilityHasToConfirmSize);

  const { fetchWellMap } = useWellMapUpdater();
  const { fetchFacilityMap } = useFacilityMapUpdater();

  // Store the function in a ref to avoid it being a dependency
  const fetchWellMapRef = useRef(fetchWellMap);
  const fetchFacilityMapRef = useRef(fetchFacilityMap);

  // Tracks the latest request version to prevent stale dispatches.
  // When filterId and facilityFilterId update at different times (separate API responses),
  // the effect fires twice. This ref ensures only the latest execution dispatches setTxnId.
  const requestVersionRef = useRef(0);

  useEffect(() => {
    fetchWellMapRef.current = fetchWellMap;
  }, [fetchWellMap]);

  useEffect(() => {
    fetchFacilityMapRef.current = fetchFacilityMap;
  }, [fetchFacilityMap]);

  const user = useSelector((state: RootState) => state.auth.user);
  const filterId = useSelector((state: RootState) => state.filter.filterId);
  const facilityFilterId = useSelector(
    (state: RootState) => state.facilityFilter.filterId
  );
  const globalNormalizeBy = useSelector(
    (state: RootState) => state.normalizeBy.globalNormalizeBy
  );
  const correctedGlobalNormalizeBy = useNormalizationUnitCorrection(globalNormalizeBy);
  const useNormalizeBy = useSelector(
    (state: RootState) => state.normalizeBy.useNormalizeBy
  );
  const userSettingsOpen = usePanelStore(selectUserSettingsOpen);

  // Group by settings
  const globalGroupBy = useSelector((state: RootState) => state.groupBy.globalGroupBy);
  const globalFacilityFocus = useFacilityGroupBy();
  const hasCheckedBins = useSelector((state: RootState) => state.groupBy.hasCheckedBins);
  const hasCheckedBinsFacility = useSelector(
    (state: RootState) => state.groupBy.hasCheckedBinsFacility
  );
  const placeholderBinsEnabled = useSelector(
    (state: RootState) => state.groupBy.placeholderBinsEnabled
  );

  // Filter settings
  const sortBy = useSelector((state: RootState) => state.filter.sortBy);
  const facilitySortBy = useSelector((state: RootState) => state.facilityFilter.sortBy);
  const showGroupsNotInFilter = useSelector(
    (state: RootState) => state.filter.showGroupsNotInFilter
  );
  const showFacilityGroupsNotInFilter = useSelector(
    (state: RootState) => state.facilityFilter.showGroupsNotInFilter
  );

  // Color palettes
  const activeColorPalette = useSelector(
    (state: RootState) => state.userSetting.activeColorPalette
  );
  const activeFacilityColorPalette = useSelector(
    (state: RootState) => state.userSetting.activeFacilityColorPalette
  );

  // Locked colors
  const lockedColors = useSelector((state: RootState) => state.app.lockedColors);

  // Significant figure settings
  const { defaultSignificantFigures, useStrictRounding } = useSelector(
    (state: RootState) => state.app.significantFiguresSettings
  );

  // Forecast trumping settings
  const specialForecastFolders = useSelector(
    (state: RootState) => state.app.userForecastSettings.specialForecastFolders,
    shallowEqual
  );
  const forecastReserveCategory = useSelector(
    (state: RootState) => state.app.userForecastSettings.reserveCategory
  );
  const checkedGlobalForecasts = useForecastStore(selectCheckedGlobalForecasts);
  const syncWells = useSelector((state: RootState) => state.app.syncWells);

  /**
   * Generates a map request object for either wells or facilities
   * Uses the generateMapRequest factory to create properly formatted requests
   */
  const getMapRequest = useCallback(
    (
      filterId: string,
      groupBy,
      sortBy,
      showGroupsNotInFilter,
      placeholderBinsEnabled,
      hasCheckedBins,
      entityKind: EntityKind
    ): Maybe<IMapRequest> => {
      if (!user) {
        return;
      }
      const isForecastTrumping = isForecastTrumpingSource(groupBy?.forecastFolder);
      const states = {
        email: user.email,
        GroupBy: isForecastTrumping
          ? {
              ...groupBy,
              forecastTrumpingSettings: buildForecastTrumpingSettings(
                checkedGlobalForecasts,
                specialForecastFolders,
                syncWells?.isSync ? syncWells.schema : undefined,
                syncWells?.isSync ? syncWells.forecastRescat : undefined,
                forecastReserveCategory
              )
            }
          : groupBy,
        FilterId: filterId,
        SortBy: sortBy,
        ShowGroupsNotInFilter: showGroupsNotInFilter,
        PlaceholderBinsEnabled: placeholderBinsEnabled,
        HasCheckedBins: hasCheckedBins,
        ColorPalette:
          entityKind === EntityKind.Facility
            ? (activeFacilityColorPalette as IColorPalette)
            : (activeColorPalette as IColorPalette),
        ReverseColor:
          entityKind === EntityKind.Facility
            ? activeFacilityColorPalette.reverse
            : activeColorPalette.reverse,
        ColorLockedItems: withLegacyLockKeys(lockedColors),
        NormalizeBySetting: Object.assign({}, correctedGlobalNormalizeBy, {
          useNormalizeBy
        }),
        DefaultSignificantFigures: defaultSignificantFigures,
        UseStrictRounding: useStrictRounding
      };

      // Use the factory to generate the request
      return generateMapRequest({ entityKind, states });
    },
    [
      user,
      activeColorPalette,
      activeFacilityColorPalette,
      lockedColors,
      correctedGlobalNormalizeBy,
      useNormalizeBy,
      defaultSignificantFigures,
      useStrictRounding,
      specialForecastFolders,
      forecastReserveCategory,
      checkedGlobalForecasts,
      syncWells
    ]
  );

  const debouncedMapRequest = useDebouncedCallback(
    async () => {
      // Snapshot the version at the moment the debounce fires.
      // The effect increments it each time a new request is signalled,
      // so if another dep change arrived while this fetch was in-flight
      // the snapshot will be stale and we discard the result below.
      const myVersion = requestVersionRef.current;

      const wellRequest = getMapRequest(
        filterId.id,
        globalGroupBy,
        sortBy,
        showGroupsNotInFilter,
        placeholderBinsEnabled,
        hasCheckedBins,
        EntityKind.Well
      );

      const facilityRequest = getMapRequest(
        facilityFilterId.id,
        globalFacilityFocus,
        facilitySortBy,
        showFacilityGroupsNotInFilter,
        placeholderBinsEnabled,
        hasCheckedBinsFacility,
        EntityKind.Facility
      );

      setWellMapRequest(wellRequest);
      setFacilityMapRequest(facilityRequest);

      // Run both requests in parallel with skipTxnIdDispatch to avoid
      // two separate setTxnId dispatches that would cause MapboxWrapper
      // to update tiles twice.
      const [facilityResult, wellResult] = await Promise.all([
        !hasToConfirmFacilitiesSize
          ? fetchFacilityMapRef.current(facilityRequest, false, {
              skipTxnIdDispatch: true
            })
          : Promise.resolve(EMPTY_MAP_RESULT),
        !hasToConfirmWellsSize
          ? fetchWellMapRef.current(wellRequest, false, { skipTxnIdDispatch: true })
          : Promise.resolve(EMPTY_MAP_RESULT)
      ]);

      // Skip dispatch if a newer request was queued while we were fetching
      if (myVersion !== requestVersionRef.current) {
        return;
      }

      // Dispatch a single combined setTxnId so MapboxWrapper only refreshes once
      setTxnId({
        ...(wellResult ?? EMPTY_MAP_RESULT),
        filterId: wellRequest?.FilterId,
        bBox: wellResult?.bBox ?? { sw: [], ne: [] },
        facilityLegend: facilityResult?.legend ?? {
          legendItems: [],
          title: ""
        },
        facilityId: facilityResult?.id || CLEAR_MAP_ID
      });
    },
    // Debounce for 100ms - waits for state to settle before making request
    100,
    {
      // Leading: false means don't fire immediately
      // Trailing: true means fire after the delay
      leading: false,
      trailing: true
    }
  );

  useEffect(() => {
    // Increment before scheduling so any response from the previous fetch
    // that completes after this point will see a stale version and discard
    // its dispatch — preventing a stale result from overwriting newer data.
    requestVersionRef.current += 1;
    debouncedMapRequest();

    // Cleanup: cancel pending requests on unmount or before next effect
    return () => {
      debouncedMapRequest.cancel();
    };
  }, [
    // debouncedMapRequest is intentionally omitted from this dep array.
    // use-debounce stores the callback in an internal ref so it always
    // closes over the latest values without needing to be re-created.
    // Including it would cause the effect to re-run on every render
    // (new function reference each time), triggering unbounded fetches.
    filterId,
    facilityFilterId,
    globalGroupBy,
    globalFacilityFocus,
    showFacilityGroupsNotInFilter,
    getMapRequest,
    useNormalizeBy,
    globalNormalizeBy,
    placeholderBinsEnabled,
    hasCheckedBins,
    hasCheckedBinsFacility,
    sortBy,
    facilitySortBy,
    showGroupsNotInFilter,
    // userSettingsOpen is included so closing the settings panel triggers a
    // fresh map request — the panel can change colour/groupBy state that
    // isn't reflected in the other deps until the panel closes.
    userSettingsOpen
  ]);

  return {
    getMapRequest
  };
}
